import React, { useEffect, useState } from 'react';
import './App.css';
import './cyber-zen.css';
import About from './components/About.js';
import Disclaimer from './components/Disclaimer.js';
import Billing from './components/Billing.js';
import HomePage from './components/HomePage.js';
import GuidedReflection from './components/GuidedReflection.js';
import ActionsPage from './components/ActionsPage.js';
import Pricing from './components/Pricing.js';
import ReflectionHistory from './components/ReflectionHistory.js';
import BehavioralPatternsHub from './components/behavioralPatterns/BehavioralPatternsHub.js';
import MentalPatternsLayout from './components/mentalPatterns/MentalPatternsLayout.js';
import MentalPatternsMentalLoad from './components/mentalPatterns/MentalPatternsMentalLoad.js';
import MentalPatternsUnderStress from './components/mentalPatterns/MentalPatternsUnderStress.js';
import MentalPatternsAnger from './components/mentalPatterns/MentalPatternsAnger.js';
import MentalPatternsGad7 from './components/mentalPatterns/MentalPatternsGad7.js';
import { GAD7_ROUTE } from './constants/gad7.js';
import { MENTAL_LOAD_INDEX_ROUTE } from './constants/wellness.js';
import {
  BEHAVIORAL_PATTERNS_ROUTE,
  DEEP_REFLECTION_ROUTE,
  ACTIONS_ROUTE,
  INTERPRETATION_PATTERNS_ROUTE,
  behavioralPatternsPath
} from './constants/routes.js';
import Faq from './components/Faq.js';
import ProfilePage from './components/ProfilePage.js';
import { EMAIL_REQUIRED_POLICY } from './constants/emailRequiredPolicy.js';
import MentalClarity from './components/MentalClarity.js';
import MentalNoise from './components/MentalNoise.js';
import StructuredThinking from './components/StructuredThinking.js';
import EmotionalAwareness from './components/EmotionalAwareness.js';
import BetterDecisions from './components/BetterDecisions.js';
import CommonQuestions from './components/CommonQuestions.js';
import CommonQuestionPage from './components/CommonQuestionPage.js';
import GuideHub from './components/GuideHub.js';
import GuideSectionPage from './components/guide/GuideSectionPage.js';
import { MENTAL_CLARITY_GUIDE } from './constants/mentalClarityGuide.js';
import MENTAL_CLARITY_SECTIONS from './components/guide/sections/index.js';
import { MENTAL_NOISE_GUIDE } from './constants/mentalNoiseGuide.js';
import MENTAL_NOISE_SECTIONS from './components/guide/mental-noise-sections/index.js';
import { getMentalNoiseSectionVisual, getMentalNoiseAccent } from './constants/mentalNoiseGuideVisuals.js';
import { STRUCTURED_THINKING_GUIDE } from './constants/structuredThinkingGuide.js';
import STRUCTURED_THINKING_SECTIONS from './components/guide/structured-thinking-sections/index.js';
import { getStructuredThinkingSectionVisual, getStructuredThinkingAccent } from './constants/structuredThinkingGuideVisuals.js';
import { EMOTIONAL_AWARENESS_GUIDE } from './constants/emotionalAwarenessGuide.js';
import EMOTIONAL_AWARENESS_SECTIONS from './components/guide/emotional-awareness-sections/index.js';
import { getEmotionalAwarenessSectionVisual, getEmotionalAwarenessAccent } from './constants/emotionalAwarenessGuideVisuals.js';
import { BETTER_DECISIONS_GUIDE } from './constants/betterDecisionsGuide.js';
import BETTER_DECISIONS_SECTIONS from './components/guide/better-decisions-sections/index.js';
import { getBetterDecisionsSectionVisual, getBetterDecisionsAccent } from './constants/betterDecisionsGuideVisuals.js';
import GetFeaturedOnPodcast from './components/GetFeaturedOnPodcast.js';
import BetaSignup from './components/BetaSignup.js';
import ProviderBeta from './components/ProviderBeta.js';
import AppShell from './components/layout/AppShell.js';
import PageContainer from './components/layout/PageContainer.js';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import BrandLogo from './components/BrandLogo.js';
import { getNetlifyFunctionsBase } from './utils/netlifyFunctions.js';
import { syncAllLocalReflectionsOnLogin } from './utils/syncLocalReflections.js';
import { applyAccessFromApi } from './utils/applyAccessFromApi.js';
import { isValidEmail, INVALID_EMAIL_MESSAGE } from './utils/emailValidation.js';
import {
    getStoredUserId,
    setStoredUserId,
    clearStoredSession,
    clearHistoryCacheForUser
} from './utils/userSession.js';
import {
    hasPendingQuickClarity,
    QUICK_CLARITY_LOGIN_INTENT
} from './utils/pendingQuickClarity.js';

/** Same-origin path only — blocks open redirects via ?next=. */
const getSafeLoginNext = (search) => {
    const next = new URLSearchParams(search).get("next");
    if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) {
        return null;
    }
    return next;
};

const RequireAuth = ({ isVerified, children }) => {
    const location = useLocation();
    if (isVerified) return children;
    const next = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
};

const LoginPage = ({ onVerified, isVerified }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const loginIntent = new URLSearchParams(location.search).get("intent");
    const loginNext = getSafeLoginNext(location.search);
    const isQuickClarityLogin =
        loginIntent === QUICK_CLARITY_LOGIN_INTENT || hasPendingQuickClarity();
    const [email, setEmail] = useState("");
    const [codeInput, setCodeInput] = useState("");
    const [loginError, setLoginError] = useState("");
    const [step, setStep] = useState("email");
    const [isSending, setIsSending] = useState(false);

    const handleSendCode = async (event) => {
        event.preventDefault();
        const trimmedEmail = email.trim();
        if (!isValidEmail(trimmedEmail)) {
            setLoginError(INVALID_EMAIL_MESSAGE);
            return;
        }
        setLoginError("");
        setIsSending(true);
        try {
            const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/sendLoginCode`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: trimmedEmail })
            });
            if (!response.ok) {
                const errText = await response.text();
                let errorMsg = "Unable to send code. Please try again.";
                try {
                    const parsed = JSON.parse(errText);
                    if (parsed?.error) {
                        errorMsg = parsed.error;
                        if (parsed?.missing) {
                            errorMsg += ` (Missing: ${parsed.missing.join(", ")})`;
                        }
                        if (parsed?.details) {
                            errorMsg += ` - ${parsed.details}`;
                        }
                    }
                } catch (_) {
                    if (errText) errorMsg = errText;
                }
                throw new Error(errorMsg);
            }
            const data = await response.json();
            if (data?.devMode && data?.code) {
                console.log(`🔐 DEV MODE: Your verification code is ${data.code}`);
                alert(`DEV MODE: Your verification code is ${data.code}\n\nCheck the browser console for details.`);
            }
            setStep("code");
        } catch (error) {
            console.error("Login code send error:", error);
            setLoginError(error?.message || "Unable to send code. Please try again.");
        } finally {
            setIsSending(false);
        }
    };

    const handleVerify = async (event) => {
        event.preventDefault();
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            setLoginError("Please enter your email address.");
            setStep("email");
            return;
        }
        if (!codeInput.trim()) {
            setLoginError("Please enter the verification code.");
            return;
        }
        try {
            const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/verifyLoginCode`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: trimmedEmail, code: codeInput.trim() })
            });
            if (!response.ok) {
                throw new Error("invalid");
            }
            let userId = null;
            try {
                const data = await response.json();
                userId = data?.userId || null;
            } catch (_) {
                /* ok: older responses may be empty */
            }
            setLoginError("");
            await onVerified(trimmedEmail, userId);
            if (isQuickClarityLogin || hasPendingQuickClarity()) {
                navigate("/");
            } else if (loginNext) {
                navigate(loginNext);
            } else {
                navigate("/history");
            }
        } catch (error) {
            setLoginError("Entered code is invalid. Please try again.");
            setCodeInput("");
            setStep("email");
        }
    };

    if (isVerified) {
        if (hasPendingQuickClarity()) {
            return <Navigate to="/" replace />;
        }
        if (loginNext) {
            return <Navigate to={loginNext} replace />;
        }
        return <Navigate to="/history" replace />;
    }

    return (
        <>
            <PageContainer className="login-box">
                <div className="brand-header">
                    <BrandLogo />
                </div>
                {step === "email" ? (
                    <>
                        <p className="login-subtext">
                            {isQuickClarityLogin
                                ? "Sign in to examine your thought and save this Quick Clarity session."
                                : "Sign in with your email to continue."}
                        </p>
                        <form className="login-form" onSubmit={handleSendCode}>
                            <input
                                type="email"
                                value={email}
                                placeholder="you@example.com"
                                onChange={(event) => setEmail(event.target.value)}
                                aria-label="Email address"
                                required
                            />
                            <button type="submit" disabled={isSending}>
                                {isSending ? "Sending..." : "Send code"}
                            </button>
                        </form>
                        <div className="login-email-policy" role="note">
                            {EMAIL_REQUIRED_POLICY.paragraphs.map((p) => (
                                <p key={p.slice(0, 40)}>{p}</p>
                            ))}
                        </div>
                    </>
                ) : null}
                {step === "code" ? (
                    <div className="code-box">
                        <p>Email is sent. Please go to your inbox and check your email for code.</p>
                        <form className="login-form" onSubmit={handleVerify}>
                            <input
                                type="text"
                                value={codeInput}
                                placeholder="6-digit code"
                                onChange={(event) => setCodeInput(event.target.value)}
                                aria-label="Verification code"
                            />
                            <button type="submit">Verify</button>
                        </form>
                    </div>
                ) : null}
                {loginError ? <p className="login-error">{loginError}</p> : null}
            </PageContainer>
            <p className="structured-thinking-note logic-layer-tagline">
              A guided framework to examine interpretations, reduce mental noise, and make better decisions.
            </p>
        </>
    );
};


const App = () => {
    const [isVerified, setIsVerified] = useState(false);
    const [verifiedEmail, setVerifiedEmail] = useState("");
    const [userId, setUserId] = useState("");
    const [personalEntries, setPersonalEntries] = useState([]);
    const [isSubscriber, setIsSubscriber] = useState(false);
    const [isPaidSubscriber, setIsPaidSubscriber] = useState(false);

    useEffect(() => {
        try {
            const storedEmail = localStorage.getItem("ahavibes_verified_email");
            const storedUserId = getStoredUserId();
            if (storedEmail) {
                setVerifiedEmail(storedEmail);
                setIsVerified(true);
            }
            if (storedUserId) setUserId(storedUserId);
            const storedSubscriber = localStorage.getItem("ahavibes_is_subscriber");
            if (storedSubscriber === "true") {
                setIsSubscriber(true);
            }
        } catch (error) {
            console.error("Unable to load saved personalized entries.", error);
        }
    }, []);

    useEffect(() => {
        setPersonalEntries([]);
    }, [verifiedEmail]);

    useEffect(() => {
        if (!isVerified || !verifiedEmail) return;
        fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/getUserInsights`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: verifiedEmail })
        })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                if (data) applyAccessFromApi(data, setIsSubscriber, setIsPaidSubscriber, setUserId);
            })
            .catch((err) => console.error("Unable to sync access on load.", err));
    }, [isVerified, verifiedEmail]);


    const handleVerified = async (email, loginUserId = null) => {
        if (loginUserId) {
            setStoredUserId(loginUserId);
            setUserId(loginUserId);
        }
        try {
            const { synced, capped } = await syncAllLocalReflectionsOnLogin(email, loginUserId || getStoredUserId());
            if (synced > 0 || capped) {
                const parts = [];
                if (synced > 0) parts.push(`Synced ${synced} on-device reflection${synced !== 1 ? "s" : ""} to your account.`);
                if (capped) parts.push("Free plan AI limit reached — become a Member to sync the rest.");
                if (parts.length) sessionStorage.setItem("ahavibes_sync_notice", parts.join(" "));
            }
        } catch (err) {
            console.error("Unable to sync local reflections to server on login.", err);
        }
        setVerifiedEmail(email);
        setIsVerified(true);
        localStorage.setItem("ahavibes_verified_email", email);
        try {
            const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/getUserInsights`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email })
            });
            if (response.ok) {
                const data = await response.json();
                applyAccessFromApi(data, setIsSubscriber, setIsPaidSubscriber, setUserId);
            }
        } catch (error) {
            console.error("Unable to sync subscriber status on login.", error);
        }
    };

    const handleLogout = () => {
        const previousUserId = userId || getStoredUserId();
        clearHistoryCacheForUser(previousUserId);
        clearStoredSession();
        setIsVerified(false);
        setVerifiedEmail("");
        setUserId("");
        setPersonalEntries([]);
        setIsSubscriber(false);
        setIsPaidSubscriber(false);
    };

    const headerProps = {
        isVerified,
        isSubscriber,
        isPaidSubscriber,
        onLogout: handleLogout,
        verifiedEmail
    };

    const providerBetaPage = (
        <AppShell headerProps={headerProps} showReviewStrip={false}>
            <ProviderBeta />
        </AppShell>
    );

    return (
          <Router>
            <Routes>
                <Route path="/" element={
                    <AppShell headerProps={headerProps}>
                        <HomePage
                            isSubscriber={isVerified ? isSubscriber : false}
                            verifiedEmail={isVerified ? verifiedEmail : null}
                            isVerified={isVerified}
                            personalEntries={personalEntries}
                            setPersonalEntries={setPersonalEntries}
                            setIsSubscriber={setIsSubscriber}
                            setIsPaidSubscriber={setIsPaidSubscriber}
                        />
                    </AppShell>
                } />
                <Route path="/login" element={
                    <AppShell headerProps={headerProps}>
                        <LoginPage onVerified={handleVerified} isVerified={isVerified} />
                    </AppShell>
                } />
                <Route path="/personalized" element={<Navigate to="/" replace />} />
                <Route
                  path={DEEP_REFLECTION_ROUTE}
                  element={
                    <AppShell headerProps={headerProps}>
                      <GuidedReflection
                        isSubscriber={isVerified ? isSubscriber : false}
                        verifiedEmail={isVerified ? verifiedEmail : null}
                      />
                    </AppShell>
                  }
                />
                <Route
                  path={ACTIONS_ROUTE}
                  element={
                    <AppShell headerProps={headerProps}>
                      <ActionsPage verifiedEmail={isVerified ? verifiedEmail : null} />
                    </AppShell>
                  }
                />
                <Route path="/about" element={<AppShell headerProps={headerProps}><About /></AppShell>} />
                <Route path="/pricing" element={<AppShell headerProps={headerProps}><Pricing verifiedEmail={isVerified ? verifiedEmail : null} isPaidSubscriber={isPaidSubscriber} /></AppShell>} />
                <Route path="/disclaimer" element={<AppShell headerProps={headerProps}><Disclaimer /></AppShell>} />
                <Route path="/faq" element={<AppShell headerProps={headerProps}><Faq /></AppShell>} />
                <Route path="/guide" element={<AppShell headerProps={headerProps}><GuideHub /></AppShell>} />
                <Route path="/guide/common-questions" element={<AppShell headerProps={headerProps}><CommonQuestions /></AppShell>} />
                <Route path="/guide/common-questions/:questionId" element={<AppShell headerProps={headerProps}><CommonQuestionPage /></AppShell>} />
                <Route path="/mental-clarity" element={<AppShell headerProps={headerProps}><MentalClarity /></AppShell>} />
                <Route path="/mental-clarity/:sectionId" element={<AppShell headerProps={headerProps}><GuideSectionPage guide={MENTAL_CLARITY_GUIDE} sectionComponents={MENTAL_CLARITY_SECTIONS} guideName="Mental Clarity Guide" nextPillar={{ path: "/mental-noise", title: "Mental Noise" }} /></AppShell>} />
                <Route path="/mental-noise" element={<AppShell headerProps={headerProps}><MentalNoise /></AppShell>} />
                <Route path="/mental-noise/:sectionId" element={<AppShell headerProps={headerProps}><GuideSectionPage guide={MENTAL_NOISE_GUIDE} sectionComponents={MENTAL_NOISE_SECTIONS} guideName="Mental Noise Guide" getVisual={getMentalNoiseSectionVisual} getAccent={getMentalNoiseAccent} nextPillar={{ path: "/structured-thinking", title: "Structured Thinking" }} /></AppShell>} />
                <Route path="/structured-thinking" element={<AppShell headerProps={headerProps}><StructuredThinking /></AppShell>} />
                <Route path="/structured-thinking/:sectionId" element={<AppShell headerProps={headerProps}><GuideSectionPage guide={STRUCTURED_THINKING_GUIDE} sectionComponents={STRUCTURED_THINKING_SECTIONS} guideName="Structured Thinking Guide" getVisual={getStructuredThinkingSectionVisual} getAccent={getStructuredThinkingAccent} nextPillar={{ path: "/emotional-awareness", title: "Emotional Awareness" }} /></AppShell>} />
                <Route path="/emotional-awareness" element={<AppShell headerProps={headerProps}><EmotionalAwareness /></AppShell>} />
                <Route path="/emotional-awareness/:sectionId" element={<AppShell headerProps={headerProps}><GuideSectionPage guide={EMOTIONAL_AWARENESS_GUIDE} sectionComponents={EMOTIONAL_AWARENESS_SECTIONS} guideName="Emotional Awareness Guide" getVisual={getEmotionalAwarenessSectionVisual} getAccent={getEmotionalAwarenessAccent} nextPillar={{ path: "/better-decisions", title: "Better Decisions" }} /></AppShell>} />
                <Route path="/better-decisions" element={<AppShell headerProps={headerProps}><BetterDecisions /></AppShell>} />
                <Route path="/better-decisions/:sectionId" element={<AppShell headerProps={headerProps}><GuideSectionPage guide={BETTER_DECISIONS_GUIDE} sectionComponents={BETTER_DECISIONS_SECTIONS} guideName="Better Decisions Guide" getVisual={getBetterDecisionsSectionVisual} getAccent={getBetterDecisionsAccent} /></AppShell>} />
                <Route
                    path="/get-featured-on-podcast"
                    element={
                        <AppShell headerProps={headerProps} showReviewStrip={false}>
                            <GetFeaturedOnPodcast />
                        </AppShell>
                    }
                />
                <Route
                    path="/beta"
                    element={
                        <AppShell headerProps={headerProps} showReviewStrip={false}>
                            <BetaSignup />
                        </AppShell>
                    }
                />
                <Route path="/provider-beta" element={providerBetaPage} />
                <Route
                    path="/profile"
                    element={
                        <RequireAuth isVerified={isVerified}>
                            <AppShell headerProps={headerProps}>
                                <ProfilePage
                                  verifiedEmail={verifiedEmail}
                                  isVerified={isVerified}
                                  onLogout={handleLogout}
                                />
                            </AppShell>
                        </RequireAuth>
                    }
                />
                <Route
                    path="/billing"
                    element={
                        <RequireAuth isVerified={isVerified}>
                            <AppShell headerProps={headerProps}>
                                <Billing verifiedEmail={verifiedEmail} />
                            </AppShell>
                        </RequireAuth>
                    }
                />
                <Route path="/reflect" element={<Navigate to="/" replace />} />
                <Route
                    path="/history"
                    element={
                        <RequireAuth isVerified={isVerified}>
                            <AppShell headerProps={headerProps}>
                                <ReflectionHistory isSubscriber={isSubscriber} verifiedEmail={verifiedEmail} />
                            </AppShell>
                        </RequireAuth>
                    }
                />
                <Route
                    path={BEHAVIORAL_PATTERNS_ROUTE}
                    element={
                        <RequireAuth isVerified={isVerified}>
                            <AppShell headerProps={headerProps}>
                                <MentalPatternsLayout verifiedEmail={verifiedEmail} isSubscriber={isSubscriber} />
                            </AppShell>
                        </RequireAuth>
                    }
                >
                    <Route index element={<BehavioralPatternsHub />} />
                    <Route path="mental-load" element={<MentalPatternsMentalLoad />} />
                    <Route path="wellness" element={<Navigate to={MENTAL_LOAD_INDEX_ROUTE} replace />} />
                    <Route path="under-stress" element={<MentalPatternsUnderStress />} />
                    <Route path="anger" element={<MentalPatternsAnger />} />
                    <Route path="gad-7" element={<MentalPatternsGad7 />} />
                </Route>
                <Route path={INTERPRETATION_PATTERNS_ROUTE} element={<Navigate to={BEHAVIORAL_PATTERNS_ROUTE} replace />} />
                <Route path="/insights" element={<Navigate to={BEHAVIORAL_PATTERNS_ROUTE} replace />} />
                <Route path="/dashboard" element={<Navigate to={BEHAVIORAL_PATTERNS_ROUTE} replace />} />
                <Route path="/mental-patterns" element={<Navigate to={BEHAVIORAL_PATTERNS_ROUTE} replace />} />
                <Route path="/mental-patterns/mental-load" element={<Navigate to={MENTAL_LOAD_INDEX_ROUTE} replace />} />
                <Route path="/mental-patterns/wellness" element={<Navigate to={MENTAL_LOAD_INDEX_ROUTE} replace />} />
                <Route path="/mental-patterns/under-stress" element={<Navigate to={behavioralPatternsPath("under-stress")} replace />} />
                <Route path="/mental-patterns/anger" element={<Navigate to={behavioralPatternsPath("anger")} replace />} />
                <Route path="/mental-patterns/gad-7" element={<Navigate to={GAD7_ROUTE} replace />} />
            </Routes>
        </Router>
    );
};

export default App;
